import { ButtonDropdown, ButtonDropdownProps } from '@amzn/awsui-components-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRefresh } from '@/src/hooks/useRefresh';
import { i18nKeys } from '@/src/utils/i18n.utils';

import styles from './AutoRefreshDropdown.module.scss';

export interface AutoRefreshIntervalOption {
  intervalMs: number;
  labelKey: string;
}

const DEFAULT_INTERVAL_OPTIONS: AutoRefreshIntervalOption[] = [
  { intervalMs: 10000, labelKey: i18nKeys.general.autoRefreshIntervals.tenSeconds },
  { intervalMs: 30000, labelKey: i18nKeys.general.autoRefreshIntervals.thirtySeconds },
  { intervalMs: 60000, labelKey: i18nKeys.general.autoRefreshIntervals.oneMinute },
  { intervalMs: 300000, labelKey: i18nKeys.general.autoRefreshIntervals.fiveMinutes },
  { intervalMs: 0, labelKey: i18nKeys.general.autoRefreshIntervals.off },
];

export interface AutoRefreshDropdownProps {
  onRefresh: () => void | Promise<void>;
  intervalOptions?: AutoRefreshIntervalOption[];
  'data-testid'?: string;
}

const AutoRefreshDropdown: React.FC<AutoRefreshDropdownProps> = ({
  onRefresh,
  intervalOptions = DEFAULT_INTERVAL_OPTIONS,
  'data-testid': testId = 'auto-refresh-dropdown',
}) => {
  const { t } = useTranslation();

  const handleRefresh = useCallback(async () => {
    await onRefresh();
  }, [onRefresh]);

  // 0 represents the "Off" option
  const [currentIntervalMs, setCurrentIntervalMs] = useState(0);

  const isActive = currentIntervalMs > 0;

  const { loading, nextRefreshAt, refresh } = useRefresh({
    intervalMs: currentIntervalMs,
    onRefresh: handleRefresh,
    enabled: isActive,
  });

  const items: ButtonDropdownProps.CheckboxItem[] = intervalOptions.map((option) => ({
    id: String(option.intervalMs),
    text: t(option.labelKey),
    itemType: 'checkbox',
    checked: option.intervalMs === currentIntervalMs,
  }));

  let intervalLabel = '';

  for (const option of intervalOptions) {
    if (option.intervalMs === currentIntervalMs) {
      intervalLabel = t(option.labelKey);
      break;
    }
  }

  const showProgress = !loading && isActive && nextRefreshAt != null;

  // Memoized so re-renders don't shift the running animation's delay
  const remainingMs = useMemo(() => {
    if (!showProgress) {
      return 0;
    }

    return Math.max(0, nextRefreshAt - Date.now());
  }, [showProgress, nextRefreshAt]);

  const barStyle: React.CSSProperties = {
    animationDuration: `${String(currentIntervalMs)}ms`,
    animationDelay: `${String(remainingMs - currentIntervalMs)}ms`,
  };

  return (
    <div className={styles.container}>
      <ButtonDropdown
        items={items}
        mainAction={{
          iconName: 'refresh',
          onClick: refresh,
          loading,
          disabled: loading,
        }}
        onItemClick={({ detail }) => {
          setCurrentIntervalMs(Number(detail.id));
        }}
        data-testid={testId}
      >
        {intervalLabel}
      </ButtonDropdown>

      {showProgress && (
        <div className={styles.track} aria-hidden="true" data-testid={`${testId}-progress`}>
          <div key={nextRefreshAt} className={styles.bar} style={barStyle} />
        </div>
      )}
    </div>
  );
};

export default AutoRefreshDropdown;
